import { useEffect, useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';

interface HeroSectionProps {
  onOpenModal: (type: 'demo' | 'start') => void;
}

export default function HeroSection({ onOpenModal }: HeroSectionProps) {
  const [visible, setVisible] = useState(false);
  const { isDark } = useTheme();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 120);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className={`relative min-h-screen flex flex-col items-center justify-center overflow-hidden ${isDark ? 'bg-[#050505]' : 'bg-white'}`}>
      {/* BG image — cinematic full bleed */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/bed51dc2e9adc1a4d0c4cd420aa994c4.jpg"
          alt=""
          className={`w-full h-full object-cover object-center transition-opacity duration-700 ${isDark ? 'opacity-35' : 'opacity-10'}`}
        />
        <div className={`absolute inset-0 ${isDark
          ? 'bg-gradient-to-b from-[#050505]/50 via-transparent to-[#050505]'
          : 'bg-gradient-to-b from-white/70 via-transparent to-white'
        }`}></div>
      </div>

      {/* Glow nodes */}
      <div className="absolute top-1/4 left-1/3 w-80 h-80 rounded-full bg-[#FF2B2B]/6 blur-3xl animate-glow-pulse pointer-events-none"></div>
      <div className="absolute bottom-1/3 right-1/4 w-96 h-96 rounded-full bg-[#3FA9F5]/6 blur-3xl animate-glow-pulse pointer-events-none" style={{ animationDelay: '1.5s' }}></div>

      {/* Content */}
      <div
        className="relative z-10 w-full max-w-5xl mx-auto px-6 text-center pt-32"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Pill badge */}
        <div className="inline-flex items-center gap-2 bg-[#FF2B2B]/10 border border-[#FF2B2B]/20 px-4 py-1.5 rounded-full mb-10">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF2B2B] animate-glow-pulse inline-block"></span>
          <span className="text-[#FF2B2B] text-xs font-semibold tracking-widest uppercase">AI-Powered Lead Systems</span>
        </div>

        {/* Giant headline — Apple style */}
        <h1
          className={`text-5xl md:text-7xl lg:text-8xl font-black leading-[1.02] tracking-tight mb-8 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}
          style={{ letterSpacing: '-0.03em' }}
        >
          Stop Losing Leads.<br />
          <span className="text-[#FF2B2B]">Start Capturing Revenue</span><br />
          Automatically.
        </h1>

        <p
          className={`text-lg md:text-2xl max-w-2xl mx-auto leading-relaxed mb-12 ${isDark ? 'text-[#C9CED6]/65' : 'text-[#6E6E73]'}`}
          style={{ fontWeight: 300 }}
        >
          A Bot Smarter builds intelligent systems that respond instantly, qualify leads,
          and book opportunities — so your business never misses what matters.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
          <button
            onClick={() => onOpenModal('start')}
            className="group bg-[#FF2B2B] hover:bg-[#e02222] text-white font-bold px-10 py-4 rounded-xl text-base transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-2"
            style={{ boxShadow: '0 8px 32px rgba(255,43,43,0.3)' }}
          >
            Get Started
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
          </button>
          <button
            onClick={() => onOpenModal('demo')}
            className={`font-semibold px-10 py-4 rounded-xl text-base transition-all duration-300 cursor-pointer whitespace-nowrap border ${
              isDark ? 'border-[#2A2A2A] text-[#C9CED6] hover:border-[#C9CED6]/30 hover:bg-white/5' : 'border-[#D2D2D7] text-[#1D1D1F] hover:bg-[#F5F5F7]'
            }`}
          >
            Book a Demo
          </button>
        </div>

        {/* Stats strip — Apple style */}
        <div className={`flex flex-wrap items-center justify-center gap-12 md:gap-20 border-t pt-12 ${isDark ? 'border-[#1A1A1A]' : 'border-[#E8E8ED]'}`}>
          {[
            { value: '24/7', label: 'Always On' },
            { value: '< 60s', label: 'First Response' },
            { value: '3x', label: 'More Conversions' },
          ].map((m) => (
            <div key={m.label} className="text-center">
              <div className={`text-4xl md:text-5xl font-black mb-1 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`} style={{ letterSpacing: '-0.03em' }}>{m.value}</div>
              <div className={`text-xs tracking-widest uppercase ${isDark ? 'text-[#C9CED6]/40' : 'text-[#6E6E73]'}`}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Hero image below text */}
      <div
        className="relative z-10 w-full mt-20"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(60px)',
          transition: 'opacity 1.2s cubic-bezier(0.16,1,0.3,1) 0.3s, transform 1.2s cubic-bezier(0.16,1,0.3,1) 0.3s',
        }}
      >
        <div className="relative w-full max-w-5xl mx-auto px-6">
          <div className="relative w-full rounded-2xl overflow-hidden" style={{ height: '420px' }}>
            <img
              src="https://readdy.ai/api/search-image?query=ultra-modern%20dark%20AI%20operations%20center%20with%20glowing%20holographic%20displays%20showing%20real-time%20lead%20data%20charts%20and%20neural%20network%20visualizations%2C%20deep%20black%20interior%2C%20red%20and%20blue%20ambient%20lighting%2C%20sleek%20minimal%20design%2C%20no%20people%2C%20cinematic%20widescreen%2C%20premium%20technology%20aesthetic&width=1200&height=500&seq=hero_dash_2&orientation=landscape"
              alt="AI System Dashboard"
              className="w-full h-full object-cover object-top"
            />
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-[#050505] via-transparent to-transparent' : 'bg-gradient-to-t from-white via-transparent to-transparent'}`}></div>
            <div className="absolute inset-0 rounded-2xl border border-white/5"></div>
            {/* Floating badge */}
            <div className={`absolute top-5 left-5 flex items-center gap-2 px-3 py-2 rounded-lg border backdrop-blur-sm ${isDark ? 'bg-[#0D0D0D]/80 border-[#1A1A1A]' : 'bg-white/80 border-[#D2D2D7]'}`}>
              <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse inline-block"></span>
              <span className={`text-xs font-semibold ${isDark ? 'text-[#C9CED6]' : 'text-[#1D1D1F]'}`}>System Live</span>
            </div>
            <div className={`absolute top-5 right-5 flex items-center gap-2 px-3 py-2 rounded-lg border backdrop-blur-sm ${isDark ? 'bg-[#0D0D0D]/80 border-[#1A1A1A]' : 'bg-white/80 border-[#D2D2D7]'}`}>
              <i className="ri-flashlight-line text-[#FF2B2B] text-sm"></i>
              <span className={`text-xs font-semibold ${isDark ? 'text-[#C9CED6]' : 'text-[#1D1D1F]'}`}>47 leads captured today</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
